import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { formatDateTimeDMY } from '../utils/dateUtils';
import { AuthContext } from '../context/AuthContext';
import ResponsiveTable from '../components/ResponsiveTable';
import { useTableSort, sortRows, makeSortTh, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from '../components/UI/tableSort';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import KpiCard from '../components/Admin/KpiCard';
import { auditsMetrics } from '../utils/adminMetrics';
import { exportToCsv } from '../utils/adminExport';
import ClientLink from '../components/ClientLink';
import LoanLink from '../components/LoanLink';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 10;

const Audits = () => {
  const { user } = useContext(AuthContext);
  const [audits, setAudits] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalAudit, setModalAudit] = useState(null);
  // A click on a column of the changed-fields table sorts it.
  const [changeSort, setChangeSort] = useState(null);

  useEffect(() => {
    const fetchAudits = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/audits?limit=1000'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        // endpoint returns array in legacy mode; support both shapes
        const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
        setAudits(data);
      } catch (err) {
        // Show server-provided message when available, otherwise the JS error
        setMessage(err.response?.data?.error || err.message || 'Failed to fetch audits');
      }
    };
    fetchAudits();
  }, []);

  const filteredAudits = audits.filter(a => {
    if (!searchTerm) return true;
    const hay = [a.action, a.entity_type, a.entity_id, a.user_id, a.user_email, a.ip_address, typeof a.details === 'string' ? a.details : JSON.stringify(a.details || {})].join(' ').toLowerCase();
    return hay.includes(searchTerm.toLowerCase());
  });

  const parseDetailsObject = (details) => {
    if (!details) return null;
    if (typeof details === 'object') return details;
    if (typeof details !== 'string') return null;
    const s = details.trim();
    if (!s) return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  const formatValue = (v) => {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    if (typeof v === 'string') return v;
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  };

  const diffBeforeAfter = (before, after, prefix = '') => {
    const changes = [];
    const isObj = (x) => x && typeof x === 'object' && !Array.isArray(x);

    // If types differ or primitive/array -> compare by JSON string
    const beforeIsObj = isObj(before);
    const afterIsObj = isObj(after);
    if (!beforeIsObj || !afterIsObj) {
      const b = formatValue(before);
      const a = formatValue(after);
      if (b !== a) changes.push({ path: prefix || '(root)', before, after });
      return changes;
    }

    const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    for (const key of keys) {
      const nextPrefix = prefix ? `${prefix}.${key}` : key;
      const bVal = before ? before[key] : undefined;
      const aVal = after ? after[key] : undefined;

      const bIsObj = isObj(bVal);
      const aIsObj = isObj(aVal);

      if (bIsObj && aIsObj) {
        changes.push(...diffBeforeAfter(bVal, aVal, nextPrefix));
        continue;
      }

      // Arrays/prim: compare stringified
      const bStr = formatValue(bVal);
      const aStr = formatValue(aVal);
      if (bStr !== aStr) changes.push({ path: nextPrefix, before: bVal, after: aVal });
    }
    return changes;
  };

  const hasBeforeAfter = (audit) => {
    const obj = parseDetailsObject(audit?.details);
    return !!(obj && typeof obj === 'object' && obj.before != null && obj.after != null);
  };

  const formatUserCell = (a) => {
    const id = a?.user_id ?? null;
    const email = (a?.user_email || '').trim();
    if (email && id != null) return `${email} (${id})`;
    if (email) return email;
    if (id != null) return String(id);
    return '-';
  };

  const totalPages = Math.max(1, Math.ceil(filteredAudits.length / PAGE_SIZE));
  // The table's rows for every match, so a click on a column sorts them all
  // before the page is taken.
  const auditRows = filteredAudits.map(a => ({
    id: a.id,
    time: a.created_at ? formatDateTimeDMY(a.created_at) : '-',
    user: formatUserCell(a),
    ip: a.ip_address || '-',
    action: a.action || '-',
    entity: a.entity_type ? `${a.entity_type}#${a.entity_id || ''}` : '-',
    details: a.details ? (typeof a.details === 'string' ? a.details : JSON.stringify(a.details)) : '-',
    raw: a
  }));
  const auditSort = useTableSort(auditRows);
  const paginated = auditSort.rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="max-w-full min-h-screen">

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3 mb-2">
        {(() => { 
          const m = auditsMetrics(audits); 
          const freq = audits.reduce((acc,a)=>{const k=a.action||'Unknown'; acc[k]=(acc[k]||0)+1; return acc;},{});
          const top = Object.entries(freq).sort((a,b)=>b[1]-a[1])[0]?.[0] || '-';
          return (
          <>
            <KpiCard label="Total Events" value={m.total} color="gray" />
            <KpiCard label="Today" value={m.today} color={m.today>0?'green':'gray'} />
            <KpiCard label="Active Users" value={m.uniqueUsers} color="blue" />
            <KpiCard label="Top Action" value={top} color="amber" />
          </>
        ); })()}
      </div>

      {message && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{message}</div>}

      {!showModal && (
      <>
      {/* Search (left) + Pagination (right) row to match ManageClients layout */}
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search audits..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="p-2 w-full sm:w-[420px]"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            onClick={() => exportToCsv('audit_trail.csv', filteredAudits.map(a=>({
              ID: a.id,
              Time: a.created_at,
              User: formatUserCell(a),
              IP: a.ip_address || '-',
              Action: a.action,
              EntityType: a.entity_type,
              EntityId: a.entity_id,
              Details: typeof a.details==='string'?a.details:JSON.stringify(a.details||{})
            })))}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200"
          >
            Export CSV
          </button>
          <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>

      {/* Responsive table using shared ResponsiveTable for consistent look */}
      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={paginated}
          sort={auditSort.sort}
          onSortChange={auditSort.onSortChange}
          columns={[
            { field: 'time', header: 'Time', sortValue: (row) => row.raw.created_at },
            { field: 'user', header: 'User' },
            { field: 'ip', header: 'IP Address' },
            { field: 'action', header: 'Action' },
            // A loan or client the event is about opens its details.
            { field: 'entity', header: 'Entity', formatter: (value, row) => {
                const type = String(row.raw.entity_type || '');
                const id = row.raw.entity_id;
                if (id && type === 'Loan') return <LoanLink loanId={id}>{value}</LoanLink>;
                if (id && type === 'Client') return <ClientLink clientId={id} name={value} />;
                return value;
              } },
            {
              field: 'details',
              header: 'Details',
              formatter: (value, row) => {
                const full = row.raw.details ? (typeof row.raw.details === 'string' ? row.raw.details : JSON.stringify(row.raw.details, null, 2)) : '';
                const preview = full.length > 120 ? `${full.slice(0, 120)}...` : full;
                return (
                  <div>
                    <pre className="whitespace-pre-wrap text-sm">{preview || '-'}</pre>
                    {full.length > 120 && (
                      <button onClick={() => { setModalAudit(row.raw); setModalContent(full); setShowModal(true); }} className="text-blue-600 text-sm mt-1 underline">Read more</button>
                    )}
                  </div>
                );
              }
            }
          ]}
          keyField="id"
          isLoading={false}
          emptyMessage="No results found."
          headerClassName="bg-gray-50 dark:bg-slate-700"
          rowClassName={(row) => {
            // Rows with before/after changes stand out; the rest keep the
            // table's own light striping, so the line under each row shows.
            if (row?.raw && hasBeforeAfter(row.raw)) return 'bg-red-50 dark:bg-red-900/30';
            return '';
          }}
        />
      </div>

      </>
      )}

  {/* Bottom pagination removed - top pagination used */}

      {/* Details Modal */}
      {showModal && (
        <FullScreenPanel
          isOpen
          onClose={() => { setShowModal(false); setModalAudit(null); }}
          title="Audit Details"
        >
            <div>
              {(() => {
                const obj = parseDetailsObject(modalAudit?.details);
                const before = obj && typeof obj === 'object' ? obj.before : null;
                const after = obj && typeof obj === 'object' ? obj.after : null;

                if (before != null && after != null) {
                  const changes = diffBeforeAfter(before, after);
                  const changeTh = makeSortTh(changeSort, setChangeSort);
                  return (
                    <div className="space-y-3">
                      <div className="text-sm text-gray-600 dark:text-slate-400">Changed fields are highlighted in light red.</div>
                      {changes.length === 0 ? (
                        <div className="text-sm text-gray-500 dark:text-slate-400">No field-level changes detected.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-full border-collapse">
                            <thead>
                              <tr className={TABLE_HEAD_ROW}>
                                {changeTh('Field', 'field', (c) => c.path, { className: `px-3 py-2 ${TABLE_HEAD_TEXT}` })}
                                {changeTh('Before', 'before', (c) => formatValue(c.before), { className: `px-3 py-2 ${TABLE_HEAD_TEXT}` })}
                                {changeTh('After', 'after', (c) => formatValue(c.after), { className: `px-3 py-2 ${TABLE_HEAD_TEXT}` })}
                              </tr>
                            </thead>
                            <tbody>
                              {sortRows(changes, changeSort).map((c) => (
                                <tr key={c.path} className={`${TABLE_ROW_LINE} bg-red-50 dark:bg-red-900/30`}>
                                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-slate-300 font-medium align-top">{c.path}</td>
                                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap align-top">{formatValue(c.before)}</td>
                                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap align-top">{formatValue(c.after)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div>
                        <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Raw details</div>
                        <pre className="whitespace-pre-wrap text-sm dark:text-slate-300">{modalContent}</pre>
                      </div>
                    </div>
                  );
                }

                return <pre className="whitespace-pre-wrap text-sm dark:text-slate-300">{modalContent}</pre>;
              })()}
            </div>
        </FullScreenPanel>
      )}
    </div>
  );
};

export default Audits;
